import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAtomValue } from "jotai";
import { Calendar, DollarSign, Percent, Target, TrendingUp } from "lucide-react";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import {
  BUSINESS_LINES,
  BUSINESS_LINE_LABELS,
  type BusinessLine,
  type BusinessLineSummary,
} from "@monitoring-panel/types";
import { useConsolidatedRevenue } from "@/hooks/useRevenue";
import { formatDate, formatNumber } from "@/lib/utils";
import { MetricBox } from "@/components/MetricBox";
import { RevenueDateRangeBar } from "@/components/revenue/RevenueDateRangeBar";
import { revenueDateRangeAtom } from "@/store/revenue";
import {
  ADVIA_CHART_OPTS,
  ADVIA_ECHARTS_THEME,
  BUSINESS_LINE_COLORS,
  REVENUE_COLORS,
  cssGradient,
  darkStop,
  solid,
  tooltipRow,
  tooltipTitle,
} from "@/lib/advia-charts";

const LINE_PATHS: Record<BusinessLine, string> = {
  "display-video": "/revenue/display-video",
  "chatgpt-ads": "/revenue/chatgpt-ads",
  geo: "/revenue/geo",
};

function marginPercentage(revenue: number, mediaCost: number): number {
  return revenue > 0 ? ((revenue - mediaCost) / revenue) * 100 : 0;
}

function targetColor(percentage: number): string {
  if (percentage >= 100) return "text-adv-success";
  if (percentage >= 80) return "text-adv-accent";
  return "text-adv-warning";
}

function BusinessLineCard({ line }: { line: BusinessLineSummary }) {
  const progress = Math.min(line.targetPercentage, 100);

  return (
    <Link
      to={LINE_PATHS[line.businessLine]}
      className="block adv-card adv-card--featured adv-card--link p-6"
    >
      <div className="flex items-center gap-2 mb-4">
        <span
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: darkStop(BUSINESS_LINE_COLORS[line.businessLine]) }}
        />
        <h3 className="text-adv-body-m font-bold text-adv-primary">{line.label}</h3>
      </div>

      <p className="adv-kpi__value text-adv-primary">{formatNumber(line.total, "euro")}</p>
      <p className="text-adv-body-s text-adv-secondary mt-1">
        Actual {formatNumber(line.revenue, "euro")} + Forecast{" "}
        {formatNumber(line.forecastedRevenue, "euro")}
      </p>

      <div className="mt-4">
        <div className="flex justify-between text-adv-body-s text-adv-secondary mb-1">
          <span>Target {formatNumber(line.target, "euro")}</span>
          <span className={`adv-num font-bold ${targetColor(line.targetPercentage)}`}>
            {line.target > 0 ? formatNumber(line.targetPercentage, "percentage", 1) : "No target"}
          </span>
        </div>
        <div className="h-2 bg-adv-sunken rounded-full overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{ width: `${progress}%`, background: cssGradient(BUSINESS_LINE_COLORS[line.businessLine]) }}
          />
        </div>
      </div>

      <p className="text-adv-body-s text-adv-secondary mt-4">
        Margin on actual revenue:{" "}
        <span className="adv-num font-bold text-adv-primary">
          {line.revenue > 0
            ? formatNumber(marginPercentage(line.revenue, line.mediaCost), "percentage", 1)
            : "—"}
        </span>
      </p>
    </Link>
  );
}

/** Every business line added up, with a card per line linking to its own page. */
export function ConsolidatedRevenueView() {
  const { startDate, endDate } = useAtomValue(revenueDateRangeAtom);
  const [shouldFetch, setShouldFetch] = useState(false);
  const { data, isLoading, error } = useConsolidatedRevenue(startDate, endDate, shouldFetch);

  const chartOption: EChartsOption = useMemo(() => {
    if (!data?.data || data.data.length === 0) return {};

    const dates = data.data.map((row) => formatDate(row.date));
    const lineSeries = BUSINESS_LINES.map((businessLine) => ({
      name: BUSINESS_LINE_LABELS[businessLine],
      type: "bar" as const,
      stack: "actual",
      data: data.data.map((row) => row.lines[businessLine].revenue),
      barWidth: "90%",
      itemStyle: { color: solid(BUSINESS_LINE_COLORS[businessLine]) },
    }));

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        formatter: (params: unknown) => {
          const items = params as Array<{
            seriesName: string;
            value: number;
            marker: string;
            axisValue: string;
          }>;
          let result = tooltipTitle(items[0].axisValue);
          items.forEach((item) => {
            if (item.value > 0 || item.seriesName === "Margin %") {
              const formattedValue =
                item.seriesName === "Margin %"
                  ? formatNumber(item.value, "percentage", 2)
                  : formatNumber(item.value, "euro");
              result += tooltipRow(item.seriesName, formattedValue, item.marker);
            }
          });
          return result;
        },
      },
      grid: { right: 16, left: 16, bottom: 8, top: 48, containLabel: true },
      legend: {
        top: 0,
        left: "center",
        itemGap: 24,
      },
      xAxis: [
        {
          type: "category",
          data: dates,
          axisTick: { alignWithLabel: true },
          axisLabel: { margin: 10, hideOverlap: true },
        },
      ],
      yAxis: [
        {
          type: "value",
          name: "Revenue (€)",
          position: "left",
          axisLabel: {
            formatter: (value: number) => formatNumber(value, "euro"),
          },
        },
        {
          type: "value",
          name: "Margin %",
          position: "right",
          axisLabel: {
            formatter: (value: number) => formatNumber(value, "percentage", 0),
          },
          splitLine: { show: false },
          min: 0,
          max: 100,
        },
      ],
      series: [
        ...lineSeries,
        {
          // Stacked on top of the lines, not overlaid: a day in progress
          // carries the revenue of the executions already loaded plus the
          // forecast of the ones still pending.
          name: "Forecasted Revenue",
          type: "bar",
          stack: "actual",
          data: data.data.map((row) => row.forecastedRevenue),
          barWidth: "90%",
          itemStyle: { color: solid(REVENUE_COLORS.forecast), borderRadius: [4, 4, 0, 0] },
        },
        {
          name: "Margin %",
          type: "line",
          yAxisIndex: 1,
          data: data.data.map((row) => marginPercentage(row.revenue, row.totalMediaCost)),
          smooth: true,
          itemStyle: { color: solid(REVENUE_COLORS.margin) },
          lineStyle: { width: 1.5, color: solid(REVENUE_COLORS.margin) },
          symbolSize: 6,
        },
      ],
    };
  }, [data]);

  return (
    <div className="space-y-6">
      <RevenueDateRangeBar
        isLoading={isLoading}
        onLoad={() => setShouldFetch(true)}
        onRangeChange={() => setShouldFetch(false)}
      />

      {error && (
        <div className="adv-alert">
          <p>Error: {(error as Error).message}</p>
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricBox
              title="Acc Revenue"
              value={data.summary.accumulatedRevenue}
              subtitle="Actual revenue (no forecast)"
              format="euro"
              icon={DollarSign}
            />
            <MetricBox
              title="Revenue Estimated"
              value={data.summary.accumulatedPlusForecasted}
              subtitle={`Includes ${formatNumber(data.summary.totalForecasted, "euro")} forecasted`}
              format="euro"
              icon={TrendingUp}
            />
            <MetricBox
              title="Target vs Actual + Forecast"
              value={data.summary.targetPercentage}
              subtitle={`${formatNumber(data.summary.accumulatedPlusForecasted, "euro")} / ${formatNumber(data.summary.targetRevenue, "euro")}`}
              format="percentage"
              icon={Target}
              valueColor={targetColor(data.summary.targetPercentage)}
            />
            <MetricBox
              title="Margin"
              value={marginPercentage(data.summary.accumulatedRevenue, data.summary.mediaCost)}
              subtitle={`Media cost ${formatNumber(data.summary.mediaCost, "euro")} on actual revenue`}
              format="percentage"
              icon={Percent}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.lines.map((line) => (
              <BusinessLineCard key={line.businessLine} line={line} />
            ))}
          </div>

          {data.data.length > 0 && (
            <div className="adv-card p-6">
              <h2 className="adv-section-title mb-6">
                Daily Revenue by Business Line (Actual & Forecasted)
              </h2>
              <ReactECharts
                option={chartOption}
                style={{ height: 560 }}
                notMerge
                theme={ADVIA_ECHARTS_THEME}
                opts={ADVIA_CHART_OPTS}
              />
            </div>
          )}
        </>
      )}

      {!shouldFetch && !data && (
        <div className="adv-card adv-state">
          <div className="adv-state__icon">
            <Calendar size={26} />
          </div>
          <p className="adv-state__desc">
            Select a date range and click "Load Data" to view revenue data
          </p>
        </div>
      )}
    </div>
  );
}
